import { datadogRum } from '@datadog/browser-rum';
import { createContext, memo, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Subscription as RxJsSubscription } from 'rxjs';
import { useAuthContext, useEndpointsContext } from '../contexts';
import { WebSocketStatus } from '../tokens/socket';
import { WebSocketClient, WebSocketPerformance, type IWebSocketClient } from './WebSocketClient';

const WS_DISCONNECT_ACTION = 'ws_disconnect';

export interface WebSocketContextProps<TMessageType> {
  client?: IWebSocketClient<TMessageType> | null;
  status: WebSocketStatus;
  performance?: any;
  /** Non-Talos clients registered via `useRegisterSocketClient`, keyed by name (e.g. `'cm'`). */
  additionalClients?: Record<string, IWebSocketClient<any>>;
  additionalStatuses?: Record<string, WebSocketStatus>;
  registerClient?: (key: string, client: IWebSocketClient<any> | null) => void;
}
export const WebSocketContext = createContext<WebSocketContextProps<unknown>>({
  status: WebSocketStatus.CONNECTING,
});
WebSocketContext.displayName = 'WebSocketContext';

/**
 * Immutably removes `key` from a record, returning the *same* reference when the key is absent so a
 * no-op update doesn't force a re-render. Shared by the client and status maps, which drop a key in
 * lockstep whenever a named client unregisters.
 */
function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) {
    return record;
  }
  const next = { ...record };
  delete next[key];
  return next;
}

/**
 * Returns the primary Talos client when called with no key, or a client registered under that key
 * via `useRegisterSocketClient` (e.g. a CoinMetrics client) when called with one.
 *
 * A key that isn't registered yet returns `null` rather than throwing — mirroring the primary client's
 * own null-while-connecting state — since registration happens asynchronously (an app-level component
 * constructs the client once its own dependencies, like an API key, become available, then registers it
 * in an effect). A consumer mounted in the same initial render as that registrar (e.g. `useSubscription`,
 * which calls this unconditionally on every render) would otherwise crash before registration has had a
 * chance to run. Only a missing `WebSocketClientProvider` entirely — a real setup bug, not a timing gap
 * — still throws, matching the no-key path below.
 */
// Overloaded rather than a single `IWebSocketClient | null` return type: the no-key path genuinely never
// returns null (it throws instead — see below), and keeping that reflected in the type is what lets the
// ~30 existing no-key call sites across the app stay exactly as they are, with no new null-checks forced
// on them. Only a call that could resolve to the keyed path — a literal key, or a `string | undefined`
// caller like `useSubscription` — gets the nullable type, matching what can actually happen at runtime.
export function useSocketClient<TMessageType = unknown>(key?: undefined): IWebSocketClient<TMessageType>;
export function useSocketClient<TMessageType = unknown>(key: string | undefined): IWebSocketClient<TMessageType> | null;
export function useSocketClient<TMessageType = unknown>(key?: string): IWebSocketClient<TMessageType> | null {
  const { client, additionalClients, registerClient } = useContext(WebSocketContext);
  if (key != null) {
    if (registerClient === undefined) {
      throw new Error('Missing WebSocketClient.Provider further up in the tree. Did you forget to add it?');
    }
    return (additionalClients?.[key] ?? null) as IWebSocketClient<TMessageType> | null;
  }
  if (client === undefined) {
    throw new Error('Missing WebSocketClient.Provider further up in the tree. Did you forget to add it?');
  }
  return client as IWebSocketClient<TMessageType>;
}

export function useSocketPerformance(): WebSocketPerformance {
  const client = useSocketClient();
  if (client.performance == null) {
    throw new Error('WebSocket performance is not initialized');
  }
  return client.performance;
}

/**
 * Status of the primary Talos client when called with no key, or of a client registered under that key
 * via `useRegisterSocketClient` (e.g. CoinMetrics) when called with one. A key that isn't registered yet
 * falls back to `CONNECTING` rather than the primary client's status — `useSubscription` gates
 * registration on this, so a named subscription must wait on *its own* client, never the primary one's.
 */
export function useSocketStatus(key?: string): WebSocketStatus {
  const { status, additionalStatuses } = useContext(WebSocketContext);
  if (key != null) {
    return additionalStatuses?.[key] ?? WebSocketStatus.CONNECTING;
  }
  return status;
}

/**
 * Registers `client` under `key` in the nearest `WebSocketClientProvider`, so `useSocketClient(key)` /
 * `useSocketStatus(key)` (and `useSubscription(request, { client: key })`) can find it. Lets an app plug
 * in a non-Talos client (e.g. CoinMetrics) from wherever its own connection dependencies (API key, auth)
 * become available, without `WebSocketClientProvider` itself needing to know about them — the provider
 * may already be mounted (and its descendants rendering) before those dependencies are ready.
 */
export function useRegisterSocketClient(key: string, client: IWebSocketClient<any> | null) {
  const { registerClient } = useContext(WebSocketContext);
  useEffect(() => {
    registerClient?.(key, client);
    return () => registerClient?.(key, null);
  }, [key, client, registerClient]);
}

type WebSocketClientProviderProps = {
  wsEndpoint?: string;
  pingPongTypes?: WebSocketClient['pingPongTypes'];
  children: ReactNode;
  wsClient?: WebSocketClient;
  enableDatadogSubscriptionStats?: boolean;
  enableDatadogWebSocketDisconnectMetric?: boolean;
  /** Statically-provided named clients, seeded at mount — mainly useful for tests. Most callers should
   * use `useRegisterSocketClient` instead, since it supports registering once its own dependencies
   * become ready, rather than requiring the client to exist before this provider mounts. */
  additionalClients?: Record<string, IWebSocketClient<any>>;
};

export const WebSocketClientProvider = memo(function WebSocketClientProvider(props: WebSocketClientProviderProps) {
  const authContext = useAuthContext();
  const { wsEndpoint } = useEndpointsContext();
  const [client, setClient] = useState<WebSocketClient | null>(null);
  const [status, setStatus] = useState<WebSocketStatus>(WebSocketStatus.CONNECTING);
  const [socketUrl, setSocketUrl] = useState<string>();
  const performance = useState<WebSocketPerformance | null>(null);

  const [additionalClients, setAdditionalClients] = useState<Record<string, IWebSocketClient<any>>>({});
  const [additionalStatuses, setAdditionalStatuses] = useState<Record<string, WebSocketStatus>>({});
  const additionalStatusSubscriptionsRef = useRef<Map<string, { onOpen: RxJsSubscription; onClose: RxJsSubscription }>>(
    new Map()
  );

  const registerClient = useCallback((key: string, nextClient: IWebSocketClient<any> | null) => {
    const existingSubscriptions = additionalStatusSubscriptionsRef.current.get(key);
    if (existingSubscriptions) {
      existingSubscriptions.onOpen.unsubscribe();
      existingSubscriptions.onClose.unsubscribe();
      additionalStatusSubscriptionsRef.current.delete(key);
    }

    if (nextClient == null) {
      setAdditionalClients(prev => withoutKey(prev, key));
      setAdditionalStatuses(prev => withoutKey(prev, key));
      return;
    }

    // Read isConnected synchronously rather than assuming CONNECTING and waiting for a *future* onOpen
    // emission: the IWebSocketClient interface only requires a plain Subject for onOpen (not a
    // ReplaySubject), so a client that already opened before this registration ran — entirely possible,
    // since a named client is typically constructed and connected by unrelated code well before
    // useRegisterSocketClient's effect happens to run — would otherwise never be caught here, staying
    // stuck at CONNECTING forever despite isConnected already being true. CoinMetricsWebSocketClient
    // works around this today with its own deliberate ReplaySubject(1), but nothing in this shared,
    // generic registration path should depend on that being every implementer's choice.
    setAdditionalStatuses(prev => ({
      ...prev,
      [key]: nextClient.isConnected ? WebSocketStatus.CONNECTED : WebSocketStatus.CONNECTING,
    }));
    const onOpen = nextClient.onOpen.subscribe(() => {
      setAdditionalStatuses(prev => ({ ...prev, [key]: WebSocketStatus.CONNECTED }));
    });
    const onClose = nextClient.onClose.subscribe(() => {
      setAdditionalStatuses(prev => ({ ...prev, [key]: WebSocketStatus.RECONNECTING }));
    });
    additionalStatusSubscriptionsRef.current.set(key, { onOpen, onClose });
    setAdditionalClients(prev => ({ ...prev, [key]: nextClient }));
  }, []);

  // Routes the static `additionalClients` prop through the exact same registration path as
  // `useRegisterSocketClient`, so a seeded client gets onOpen/onClose subscriptions and a real
  // additionalStatuses entry too — otherwise it would only ever appear in `additionalClients` with no
  // matching status, permanently stuck at useSocketStatus's CONNECTING fallback (which useSubscription
  // gates registration on) regardless of whether the client itself ever actually opens. Runs once on
  // mount only: this prop is a static, test-only seed (see its own doc comment), not meant to react to
  // later prop changes the way `useRegisterSocketClient` reacts to a changing client instance.
  useEffect(() => {
    for (const [key, initialClient] of Object.entries(props.additionalClients ?? {})) {
      registerClient(key, initialClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Belt-and-suspenders: if the provider itself unmounts while named clients are still registered
  // (registerClient's own per-key cleanup runs when the *consumer* unmounts, not this provider), make
  // sure we don't leak their onOpen/onClose subscriptions.
  useEffect(() => {
    const subscriptionsByKey = additionalStatusSubscriptionsRef.current;
    return () => {
      for (const subscriptions of subscriptionsByKey.values()) {
        subscriptions.onOpen.unsubscribe();
        subscriptions.onClose.unsubscribe();
      }
      subscriptionsByKey.clear();
    };
  }, []);

  useEffect(() => {
    if (authContext.isAuthenticated) {
      const urlWithVersion = `${wsEndpoint}/${import.meta.env.VITE_GIT_HASH}`;
      setSocketUrl(urlWithVersion);
    }
  }, [authContext, wsEndpoint]);

  const isAuthenticated = authContext?.isAuthenticated;
  useEffect(() => {
    if (socketUrl && isAuthenticated) {
      const performance = new WebSocketPerformance({
        enableDatadogSubscriptionStats: props.enableDatadogSubscriptionStats,
      });
      const client =
        props.wsClient ??
        new WebSocketClient({
          pingPongTypes: props.pingPongTypes,
        });
      client.performance = performance;
      const onOpen = client.onOpen.subscribe(() => {
        setStatus(WebSocketStatus.CONNECTED);
      });
      const onClose = client.onClose.subscribe(e => {
        setStatus(WebSocketStatus.RECONNECTING);
        if (props.enableDatadogWebSocketDisconnectMetric) {
          datadogRum.addAction(WS_DISCONNECT_ACTION, {
            code: e.code,
            reason: e.reason,
            wasClean: e.wasClean,
          });
        }
      });
      client.connect(socketUrl);
      performance.start();
      setClient(client);
      return () => {
        performance.stop();
        client.close();
        onOpen.unsubscribe();
        onClose.unsubscribe();
        setClient(null);
      };
    } else {
      setClient(null);
    }
  }, [
    isAuthenticated,
    socketUrl,
    props.pingPongTypes,
    props.wsClient,
    props.enableDatadogSubscriptionStats,
    props.enableDatadogWebSocketDisconnectMetric,
  ]);

  return (
    <WebSocketContext.Provider
      value={{ client, status, performance, additionalClients, additionalStatuses, registerClient }}
    >
      {props.children}
    </WebSocketContext.Provider>
  );
});
